class ReviewsSlider {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector(".reviews-slider__slider");
    this.slides = section.querySelectorAll(".reviews-slider__slide");
    this.prevButton = section.querySelector(".reviews-slider__nav--prev");
    this.nextButton = section.querySelector(".reviews-slider__nav--next");
    this.currentSlide = 0;
    this.autoplayInterval = null;
    this.autoplayDelay = 7000;

    if (this.slides.length > 0) {
      this.init();
    }
  }

  init() {
    // Show first slide
    this.showSlide(0);

    // Set slider height based on tallest slide
    this.setSliderHeight();

    // Add event listeners
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.prevSlide());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => this.nextSlide());
    }

    // Add keyboard navigation
    this.section.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        this.prevSlide();
      } else if (e.key === "ArrowRight") {
        this.nextSlide();
      }
    });

    // Add touch support for mobile
    this.addTouchSupport();

    // Start autoplay if more than one slide
    if (this.slides.length > 1) {
      this.startAutoplay();
    }

    // Pause autoplay on hover
    this.section.addEventListener("mouseenter", () => this.stopAutoplay());
    this.section.addEventListener("mouseleave", () => this.startAutoplay());

    // Refresh height on window resize
    this.resizeHandler = this.debounce(() => {
      this.setSliderHeight();
    }, 300);
    window.addEventListener("resize", this.resizeHandler);
  }

  showSlide(index) {
    // Fade out current slide
    this.slides.forEach((slide) =>
      slide.classList.remove("reviews-slider__slide--active")
    );

    // Wait for fade out, then fade in new slide
    setTimeout(() => {
      if (this.slides[index]) {
        this.slides[index].classList.add("reviews-slider__slide--active");
      }
    }, 50);

    this.currentSlide = index;
  }

  nextSlide() {
    let nextIndex = this.currentSlide + 1;
    if (nextIndex >= this.slides.length) {
      nextIndex = 0;
    }
    this.showSlide(nextIndex);
    this.resetAutoplay();
  }

  prevSlide() {
    let prevIndex = this.currentSlide - 1;
    if (prevIndex < 0) {
      prevIndex = this.slides.length - 1;
    }
    this.showSlide(prevIndex);
    this.resetAutoplay();
  }

  goToSlide(index) {
    this.showSlide(index);
    this.resetAutoplay();
  }

  startAutoplay() {
    if (this.slides.length <= 1) return;

    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  addTouchSupport() {
    let startX = 0;
    let endX = 0;
    let isDragging = false;
    const minSwipeDistance = 50;

    // Touch events for mobile
    this.slider.addEventListener(
      "touchstart",
      (e) => {
        startX = e.changedTouches[0].screenX;
        isDragging = true;
      },
      { passive: true }
    );

    this.slider.addEventListener(
      "touchmove",
      (e) => {
        if (!isDragging) return;
        endX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.slider.addEventListener(
      "touchend",
      (e) => {
        if (!isDragging) return;
        isDragging = false;
        this.handleSwipe(startX, endX, minSwipeDistance);
      },
      { passive: true }
    );

    // Mouse events for desktop
    this.slider.addEventListener("mousedown", (e) => {
      startX = e.pageX;
      isDragging = true;
      this.slider.style.cursor = "grabbing";
      e.preventDefault();
    });

    this.slider.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      endX = e.pageX;
    });

    this.slider.addEventListener("mouseup", (e) => {
      if (!isDragging) return;
      isDragging = false;
      this.slider.style.cursor = "grab";
      this.handleSwipe(startX, endX, minSwipeDistance);
    });

    this.slider.addEventListener("mouseleave", (e) => {
      if (!isDragging) return;
      isDragging = false;
      this.slider.style.cursor = "grab";
      this.handleSwipe(startX, endX, minSwipeDistance);
    });

    // Set initial cursor
    this.slider.style.cursor = "grab";
  }

  handleSwipe(startX, endX, minDistance) {
    const distance = endX - startX;

    if (Math.abs(distance) >= minDistance) {
      if (distance > 0) {
        // Swipe right - go to previous slide
        this.prevSlide();
      } else {
        // Swipe left - go to next slide
        this.nextSlide();
      }
    }
  }

  setSliderHeight() {
    let maxHeight = 0;

    // Temporarily show all slides to measure their height
    this.slides.forEach((slide) => {
      slide.style.position = "relative";
      slide.style.opacity = "1";
      slide.style.pointerEvents = "auto";

      const slideHeight = slide.offsetHeight;
      if (slideHeight > maxHeight) {
        maxHeight = slideHeight;
      }

      // Reset to original state
      slide.style.position = "";
      slide.style.opacity = "";
      slide.style.pointerEvents = "";
    });

    // Set the slider to the max height
    if (maxHeight > 0) {
      this.slider.style.minHeight = maxHeight + "px";
    }

    // Reapply active state to first slide
    if (this.slides.length > 0) {
      this.slides[0].classList.add("reviews-slider__slide--active");
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize all review sliders on the page
document.addEventListener("DOMContentLoaded", () => {
  const sections = document.querySelectorAll(".reviews-slider");
  sections.forEach((section) => {
    new ReviewsSlider(section);
  });
});

// Reinitialize on Shopify theme editor section updates
if (Shopify && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target.querySelector(".reviews-slider");
    if (section) {
      new ReviewsSlider(section);
    }
  });
}
